/**
 * Centralized localStorage read/write for the self-service feature, with
 * transparent compression.
 *
 * WHY THIS EXISTS
 * ---------------
 * Self-service charts and dashboards embed their full uploaded dataset (every
 * row) into localStorage. Several keys (savedCharts, savedDashboards,
 * dashboardDraft) are written from more than one service, and the dataset rows
 * are duplicated across them. On large uploads this blows the browser's ~5MB
 * per-origin localStorage quota and setItem throws QuotaExceededError, surfacing
 * as "Unable to save chart locally. The uploaded dataset may be too large...".
 *
 * These helpers compress every payload with a small, dependency-free LZ codec
 * (the lz-string algorithm, inlined) before writing, and decompress on read.
 * JSON of tabular data compresses ~10-15x, which - combined with removing the
 * duplicate writers - restores plenty of headroom.
 *
 * BACKWARD COMPATIBILITY
 * ----------------------
 * Existing users already have UNCOMPRESSED JSON under these keys. `readJson`
 * handles that with a "try plain JSON first" strategy: legacy values are valid
 * JSON (they start with `{`/`[`), so `JSON.parse` succeeds on them directly;
 * compressed payloads are not valid JSON, so parse throws and we fall through to
 * decompress. The first write after this change re-stores the value compressed,
 * so old data upgrades in place with no migration step.
 */

/**
 * Write a value as compressed JSON under `key`. Returns true on success.
 * On QuotaExceededError (or any storage failure) returns false WITHOUT throwing,
 * so callers can surface a friendly message.
 *
 * SELF-HEALING ON QUOTA
 * ---------------------
 * localStorage quota is shared across ALL keys for the origin. Self-service
 * charts cache their full dataset (every row) under savedCharts, so once that
 * key fills the ~5MB budget, even a small write (savedDashboards, dashboardDraft)
 * fails. `reclaimKeys` lets the caller offer keys this write may EVICT to make
 * room, in priority order (least-valuable first). On a quota failure we remove
 * them one at a time and retry after each removal, so a legitimate save still
 * lands instead of hard-failing. A key equal to `key` itself is skipped (we
 * never delete what we're trying to write). Eviction only touches disposable
 * caches the caller nominates — never arbitrary keys.
 */
export function writeJson(key: string, value: unknown, reclaimKeys: string[] = []): boolean {
  let compressed: string;
  try {
    compressed = compressToUTF16(JSON.stringify(value));
  } catch {
    return false;
  }
  if (trySetItem(key, compressed)) return true;

  // Quota exhausted. Evict caller-nominated disposable caches and retry.
  for (const reclaimKey of reclaimKeys) {
    if (reclaimKey === key) continue;
    try {
      if (window.localStorage.getItem(reclaimKey) == null) continue;
      window.localStorage.removeItem(reclaimKey);
    } catch {
      continue;
    }
    if (trySetItem(key, compressed)) return true;
  }
  return false;
}

/** setItem wrapped so QuotaExceededError (and any storage failure) is a boolean,
 *  not a throw. */
function trySetItem(key: string, value: string): boolean {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

/** Approximate size (in bytes) a value occupies in localStorage once compressed,
 *  so callers can measure/budget before writing. localStorage counts UTF-16 code
 *  units (2 bytes each). Returns 0 if the value can't be serialized. */
export function measureJsonSize(value: unknown): number {
  try {
    return compressToUTF16(JSON.stringify(value)).length * 2;
  } catch {
    return 0;
  }
}

/**
 * Read and parse a value written by `writeJson`. Transparently handles both the
 * new compressed payloads AND legacy uncompressed JSON written before this change
 * (so existing users' data keeps loading with no migration step).
 */
export function readJson<T>(key: string, fallback: T): T {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    return fallback;
  }
  if (raw == null || raw === '') return fallback;

  // Legacy plaintext JSON parses directly. Compressed payloads are not valid
  // JSON, so this throws and we fall through to decompress.
  try {
    return JSON.parse(raw) as T;
  } catch {
    /* not plain JSON - fall through to decompress */
  }

  try {
    const json = decompressFromUTF16(raw);
    return json == null ? fallback : (JSON.parse(json) as T);
  } catch {
    return fallback;
  }
}

/* ------------------------------------------------------------------------- *
 * Dynamic-dashboard layout cache (dvm.dynamicDashboard.<slug>)
 *
 * These per-dashboard caches hold a full published `layout_json` (with baked
 * datasets). Two writers create one per dashboard the user owns/opens, and they
 * were previously written UNCOMPRESSED and NEVER bounded — so on a heavy user
 * they grew to ~10MB and exhausted the shared localStorage quota, which then
 * made every OTHER write (saving a dashboard, a chart, the draft) fail with
 * QuotaExceededError. These caches are pure caches — the API is the source of
 * truth and both readers fall back to a fresh fetch — so we can compress them
 * and cap how many we keep.
 * ------------------------------------------------------------------------- */

/** Prefix all dynamic-dashboard layout caches share (key = `${PREFIX}.<slug>`). */
const DYNAMIC_DASHBOARD_CACHE_PREFIX = 'dvm.dynamicDashboard';

/** Keep at most this many per-dashboard caches. On write, the least-recently
 *  written entries beyond this are evicted. Chosen so a normal working set of
 *  dashboards stays cached while a long tail can't exhaust the quota. */
const DYNAMIC_DASHBOARD_CACHE_LIMIT = 8;

/** Keys that are NOT slug caches even though they share the prefix — never
 *  evict/purge these. `dvm.dynamicDashboard.id` is the active-dashboard id. */
const DYNAMIC_DASHBOARD_RESERVED_SUFFIXES = new Set(['id']);

function isDynamicDashboardSlugKey(key: string): boolean {
  if (!key.startsWith(DYNAMIC_DASHBOARD_CACHE_PREFIX + '.')) return false;
  const suffix = key.slice(DYNAMIC_DASHBOARD_CACHE_PREFIX.length + 1);
  return suffix.length > 0 && !DYNAMIC_DASHBOARD_RESERVED_SUFFIXES.has(suffix);
}

/** All dynamic-dashboard slug cache keys currently in localStorage. */
function dynamicDashboardCacheKeys(): string[] {
  const keys: string[] = [];
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && isDynamicDashboardSlugKey(key)) keys.push(key);
    }
  } catch { /* storage unavailable */ }
  return keys;
}

/**
 * Write one dynamic-dashboard layout cache, COMPRESSED, keeping at most
 * DYNAMIC_DASHBOARD_CACHE_LIMIT slug caches (LRU: the key being written becomes
 * most-recent; oldest others are evicted first when over the cap or on quota
 * pressure). Returns true on success. Best-effort — a cache miss just means the
 * reader re-fetches from the API, so failure is non-fatal.
 */
/**
 * DEFERRED, COALESCED cache write.
 *
 * `writeJson` runs `compressToUTF16` — a pure-JS, per-character LZ loop — over a
 * layout_json that embeds the full dataset. On a real dashboard that is seconds
 * of BLOCKED main thread, and it fired on every dashboard open and on every
 * debounced auto-persist: the page froze, then came back, then froze again.
 *
 * This is a best-effort cache (readers fall back to the API), so it has no
 * business blocking anything. Work is pushed to idle time and coalesced per
 * slug, so rapid callers (slider auto-persist) compress once, not once per call.
 * No caller reads the old boolean return.
 */
const pendingCacheWrites = new Map<string, unknown>();
let cacheFlushHandle: number | null = null;

/** Above this JSON length, compression costs more than the cache is worth. */
const DYNAMIC_DASHBOARD_CACHE_MAX_CHARS = 2_000_000;

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
};

function scheduleCacheFlush(): void {
  if (cacheFlushHandle != null) return;
  const idleWindow = window as IdleWindow;
  const run = () => {
    cacheFlushHandle = null;
    const entries = Array.from(pendingCacheWrites.entries());
    pendingCacheWrites.clear();
    for (const [slug, layoutJson] of entries) {
      writeDynamicDashboardCacheNow(slug, layoutJson);
    }
  };
  cacheFlushHandle = idleWindow.requestIdleCallback
    ? idleWindow.requestIdleCallback(run, { timeout: 3000 })
    : (setTimeout(run, 500) as unknown as number);
}

export function writeDynamicDashboardCache(slug: string, layoutJson: unknown): void {
  pendingCacheWrites.set(slug, layoutJson);
  scheduleCacheFlush();
}

/** Synchronous write — only for the deferred flush above (and tests). */
export function writeDynamicDashboardCacheNow(slug: string, layoutJson: unknown): boolean {
  const key = `${DYNAMIC_DASHBOARD_CACHE_PREFIX}.${slug}`;

  // Bail before compressing something huge — that cost is the whole problem.
  try {
    if (JSON.stringify(layoutJson)?.length > DYNAMIC_DASHBOARD_CACHE_MAX_CHARS) {
      try { window.localStorage.removeItem(key); } catch { /* ignore */ }
      return false;
    }
  } catch {
    return false;
  }

  // Evict down to the cap BEFORE writing (excluding the key we're about to
  // write, which we want to keep). Oldest-first by insertion order in the
  // localStorage index (good enough LRU proxy: re-writing a key doesn't move it,
  // but the working set naturally cycles).
  const existing = dynamicDashboardCacheKeys().filter((k) => k !== key);
  const overflow = existing.length - (DYNAMIC_DASHBOARD_CACHE_LIMIT - 1);
  const reclaimKeys: string[] = [];
  if (overflow > 0) reclaimKeys.push(...existing.slice(0, overflow));
  // Also nominate ALL other slug caches as reclaimable on quota pressure, so a
  // large write can always make room by shedding other caches before failing.
  for (const k of existing) if (!reclaimKeys.includes(k)) reclaimKeys.push(k);

  // Proactively remove the over-cap keys (writeJson only removes on quota error).
  if (overflow > 0) {
    for (const k of existing.slice(0, overflow)) {
      try { window.localStorage.removeItem(k); } catch { /* ignore */ }
    }
  }

  return writeJson(key, layoutJson, reclaimKeys);
}

/** Read one dynamic-dashboard layout cache (handles compressed + legacy plain). */
export function readDynamicDashboardCache<T>(slug: string, fallback: T): T {
  return readJson<T>(`${DYNAMIC_DASHBOARD_CACHE_PREFIX}.${slug}`, fallback);
}

/* ---------------------------------------------------------------------------
 * JUST-PUBLISHED HANDOFF
 *
 * `dashboard_layouts/dynamic_filter` is eventually consistent: for a short
 * window after a publish it still returns the PREVIOUS version of the row, so
 * the redirect straight into the published view rendered stale content and only
 * a manual refresh showed the change.
 *
 * The publisher hands the layout it just wrote directly to the published view
 * through this one-shot slot, so a publish shows publish-time truth and every
 * later load goes to the API as normal. Deliberately NOT the deferred
 * `writeDynamicDashboardCache` above — that flushes on idle (up to 500ms /
 * requestIdleCallback), which loses the race with the navigation it is meant to
 * cover. This write is synchronous and uncompressed for the same reason.
 * ------------------------------------------------------------------------- */

const JUST_PUBLISHED_KEY = 'dvm.dynamicDashboard.justPublished';

/** How long a handoff stays valid. Long enough to cover the redirect, short
 *  enough that a marker orphaned by a crashed navigation can't mask real API
 *  data on a later visit. */
const JUST_PUBLISHED_TTL_MS = 60_000;

/** Publisher side: stash the layout that was just written for `slug`. */
export function markDashboardJustPublished(slug: string, layoutJson: unknown): void {
  if (!slug) return;
  try {
    window.localStorage.setItem(JUST_PUBLISHED_KEY, JSON.stringify({
      slug,
      at: Date.now(),
      layoutJson,
    }));
  } catch { /* quota / unavailable — the API path still works, just staler */ }
}

/**
 * Reader side: ONE-SHOT. Returns the just-published layout for `slug` and
 * clears the slot, so this only ever affects the single load that follows a
 * publish. Returns null when there is no handoff, it belongs to another
 * dashboard, or it has expired.
 */
export function consumeJustPublishedDashboard<T = unknown>(slug: string): T | null {
  if (!slug) return null;
  let raw: string | null = null;
  try { raw = window.localStorage.getItem(JUST_PUBLISHED_KEY); } catch { return null; }
  if (!raw) return null;
  // Clear FIRST: a malformed or foreign entry must not be able to wedge this
  // path permanently.
  try { window.localStorage.removeItem(JUST_PUBLISHED_KEY); } catch { /* ignore */ }
  try {
    const parsed = JSON.parse(raw) as { slug?: string; at?: number; layoutJson?: unknown };
    if (String(parsed?.slug ?? '') !== slug) return null;
    const at = Number(parsed?.at);
    if (!Number.isFinite(at) || Date.now() - at > JUST_PUBLISHED_TTL_MS) return null;
    const layout = parsed?.layoutJson;
    return layout && typeof layout === 'object' ? (layout as T) : null;
  } catch {
    return null;
  }
}

/**
 * One-time cleanup for existing users: remove LEGACY (uncompressed) dynamic
 * dashboard caches and trim the set to the cap. Uncompressed entries are the
 * ~10MB of bloat that broke saving; they'll be re-cached (compressed) the next
 * time each dashboard is opened. Safe to call on every app load — it's cheap and
 * idempotent. Returns the number of keys removed.
 */
export function purgeLegacyDynamicDashboardCaches(): number {
  let removed = 0;
  const keys = dynamicDashboardCacheKeys();
  for (const key of keys) {
    let raw: string | null = null;
    try { raw = window.localStorage.getItem(key); } catch { continue; }
    if (raw == null) continue;
    // Legacy uncompressed entries are valid JSON starting with { or [.
    // Compressed entries are not — leave those (they're already small).
    const trimmed = raw.trimStart();
    const looksJson = trimmed.startsWith('{') || trimmed.startsWith('[');
    if (looksJson) {
      try { window.localStorage.removeItem(key); removed++; } catch { /* ignore */ }
    }
  }
  return removed;
}

/* ------------------------------------------------------------------------- *
 * lz-string (UTF-16 variant), inlined.
 *
 * Public-domain implementation by Pieroxy (https://github.com/pieroxy/lz-string,
 * WTFPL/MIT). Only the UTF-16 compress/decompress pair is included - it packs 15
 * bits per UTF-16 char, which localStorage stores efficiently (localStorage
 * counts UTF-16 code units, so this yields real savings vs. raw JSON).
 * ------------------------------------------------------------------------- */

function compressToUTF16(input: string): string {
  if (input == null) return '';
  return _compress(input, 15, (a) => String.fromCharCode(a + 32)) + ' ';
}

function decompressFromUTF16(compressed: string): string | null {
  if (compressed == null) return '';
  if (compressed === '') return null;
  return _decompress(compressed.length, 16384, (index) => compressed.charCodeAt(index) - 32);
}

function _compress(uncompressed: string, bitsPerChar: number, getCharFromInt: (n: number) => string): string {
  if (uncompressed == null) return '';
  let i: number, value: number;
  const contextDictionary: Record<string, number> = {};
  const contextDictionaryToCreate: Record<string, boolean> = {};
  let contextC = '';
  let contextWC = '';
  let contextW = '';
  let contextEnlargeIn = 2;
  let contextDictSize = 3;
  let contextNumBits = 2;
  const contextData: string[] = [];
  let contextDataVal = 0;
  let contextDataPosition = 0;

  for (let ii = 0; ii < uncompressed.length; ii += 1) {
    contextC = uncompressed.charAt(ii);
    if (!Object.prototype.hasOwnProperty.call(contextDictionary, contextC)) {
      contextDictionary[contextC] = contextDictSize++;
      contextDictionaryToCreate[contextC] = true;
    }
    contextWC = contextW + contextC;
    if (Object.prototype.hasOwnProperty.call(contextDictionary, contextWC)) {
      contextW = contextWC;
    } else {
      if (Object.prototype.hasOwnProperty.call(contextDictionaryToCreate, contextW)) {
        if (contextW.charCodeAt(0) < 256) {
          for (i = 0; i < contextNumBits; i++) {
            contextDataVal = contextDataVal << 1;
            if (contextDataPosition === bitsPerChar - 1) {
              contextDataPosition = 0;
              contextData.push(getCharFromInt(contextDataVal));
              contextDataVal = 0;
            } else {
              contextDataPosition++;
            }
          }
          value = contextW.charCodeAt(0);
          for (i = 0; i < 8; i++) {
            contextDataVal = (contextDataVal << 1) | (value & 1);
            if (contextDataPosition === bitsPerChar - 1) {
              contextDataPosition = 0;
              contextData.push(getCharFromInt(contextDataVal));
              contextDataVal = 0;
            } else {
              contextDataPosition++;
            }
            value = value >> 1;
          }
        } else {
          value = 1;
          for (i = 0; i < contextNumBits; i++) {
            contextDataVal = (contextDataVal << 1) | value;
            if (contextDataPosition === bitsPerChar - 1) {
              contextDataPosition = 0;
              contextData.push(getCharFromInt(contextDataVal));
              contextDataVal = 0;
            } else {
              contextDataPosition++;
            }
            value = 0;
          }
          value = contextW.charCodeAt(0);
          for (i = 0; i < 16; i++) {
            contextDataVal = (contextDataVal << 1) | (value & 1);
            if (contextDataPosition === bitsPerChar - 1) {
              contextDataPosition = 0;
              contextData.push(getCharFromInt(contextDataVal));
              contextDataVal = 0;
            } else {
              contextDataPosition++;
            }
            value = value >> 1;
          }
        }
        contextEnlargeIn--;
        if (contextEnlargeIn === 0) {
          contextEnlargeIn = Math.pow(2, contextNumBits);
          contextNumBits++;
        }
        delete contextDictionaryToCreate[contextW];
      } else {
        value = contextDictionary[contextW];
        for (i = 0; i < contextNumBits; i++) {
          contextDataVal = (contextDataVal << 1) | (value & 1);
          if (contextDataPosition === bitsPerChar - 1) {
            contextDataPosition = 0;
            contextData.push(getCharFromInt(contextDataVal));
            contextDataVal = 0;
          } else {
            contextDataPosition++;
          }
          value = value >> 1;
        }
      }
      contextEnlargeIn--;
      if (contextEnlargeIn === 0) {
        contextEnlargeIn = Math.pow(2, contextNumBits);
        contextNumBits++;
      }
      contextDictionary[contextWC] = contextDictSize++;
      contextW = String(contextC);
    }
  }

  if (contextW !== '') {
    if (Object.prototype.hasOwnProperty.call(contextDictionaryToCreate, contextW)) {
      if (contextW.charCodeAt(0) < 256) {
        for (i = 0; i < contextNumBits; i++) {
          contextDataVal = contextDataVal << 1;
          if (contextDataPosition === bitsPerChar - 1) {
            contextDataPosition = 0;
            contextData.push(getCharFromInt(contextDataVal));
            contextDataVal = 0;
          } else {
            contextDataPosition++;
          }
        }
        value = contextW.charCodeAt(0);
        for (i = 0; i < 8; i++) {
          contextDataVal = (contextDataVal << 1) | (value & 1);
          if (contextDataPosition === bitsPerChar - 1) {
            contextDataPosition = 0;
            contextData.push(getCharFromInt(contextDataVal));
            contextDataVal = 0;
          } else {
            contextDataPosition++;
          }
          value = value >> 1;
        }
      } else {
        value = 1;
        for (i = 0; i < contextNumBits; i++) {
          contextDataVal = (contextDataVal << 1) | value;
          if (contextDataPosition === bitsPerChar - 1) {
            contextDataPosition = 0;
            contextData.push(getCharFromInt(contextDataVal));
            contextDataVal = 0;
          } else {
            contextDataPosition++;
          }
          value = 0;
        }
        value = contextW.charCodeAt(0);
        for (i = 0; i < 16; i++) {
          contextDataVal = (contextDataVal << 1) | (value & 1);
          if (contextDataPosition === bitsPerChar - 1) {
            contextDataPosition = 0;
            contextData.push(getCharFromInt(contextDataVal));
            contextDataVal = 0;
          } else {
            contextDataPosition++;
          }
          value = value >> 1;
        }
      }
      contextEnlargeIn--;
      if (contextEnlargeIn === 0) {
        contextEnlargeIn = Math.pow(2, contextNumBits);
        contextNumBits++;
      }
      delete contextDictionaryToCreate[contextW];
    } else {
      value = contextDictionary[contextW];
      for (i = 0; i < contextNumBits; i++) {
        contextDataVal = (contextDataVal << 1) | (value & 1);
        if (contextDataPosition === bitsPerChar - 1) {
          contextDataPosition = 0;
          contextData.push(getCharFromInt(contextDataVal));
          contextDataVal = 0;
        } else {
          contextDataPosition++;
        }
        value = value >> 1;
      }
    }
    contextEnlargeIn--;
    if (contextEnlargeIn === 0) {
      contextEnlargeIn = Math.pow(2, contextNumBits);
      contextNumBits++;
    }
  }

  value = 2;
  for (i = 0; i < contextNumBits; i++) {
    contextDataVal = (contextDataVal << 1) | (value & 1);
    if (contextDataPosition === bitsPerChar - 1) {
      contextDataPosition = 0;
      contextData.push(getCharFromInt(contextDataVal));
      contextDataVal = 0;
    } else {
      contextDataPosition++;
    }
    value = value >> 1;
  }

  for (;;) {
    contextDataVal = contextDataVal << 1;
    if (contextDataPosition === bitsPerChar - 1) {
      contextData.push(getCharFromInt(contextDataVal));
      break;
    } else contextDataPosition++;
  }
  return contextData.join('');
}

function _decompress(length: number, resetValue: number, getNextValue: (index: number) => number): string | null {
  const dictionary: Array<string | number> = [];
  let enlargeIn = 4;
  let dictSize = 4;
  let numBits = 3;
  let entry = '';
  const result: string[] = [];
  let w: string;
  let bits: number, resb: number, maxpower: number, power: number;
  let c: string | number = '';
  const data = { val: getNextValue(0), position: resetValue, index: 1 };

  for (let i = 0; i < 3; i += 1) {
    dictionary[i] = i;
  }

  bits = 0;
  maxpower = Math.pow(2, 2);
  power = 1;
  while (power !== maxpower) {
    resb = data.val & data.position;
    data.position >>= 1;
    if (data.position === 0) {
      data.position = resetValue;
      data.val = getNextValue(data.index++);
    }
    bits |= (resb > 0 ? 1 : 0) * power;
    power <<= 1;
  }

  switch (bits) {
    case 0:
      bits = 0;
      maxpower = Math.pow(2, 8);
      power = 1;
      while (power !== maxpower) {
        resb = data.val & data.position;
        data.position >>= 1;
        if (data.position === 0) {
          data.position = resetValue;
          data.val = getNextValue(data.index++);
        }
        bits |= (resb > 0 ? 1 : 0) * power;
        power <<= 1;
      }
      c = String.fromCharCode(bits);
      break;
    case 1:
      bits = 0;
      maxpower = Math.pow(2, 16);
      power = 1;
      while (power !== maxpower) {
        resb = data.val & data.position;
        data.position >>= 1;
        if (data.position === 0) {
          data.position = resetValue;
          data.val = getNextValue(data.index++);
        }
        bits |= (resb > 0 ? 1 : 0) * power;
        power <<= 1;
      }
      c = String.fromCharCode(bits);
      break;
    case 2:
      return '';
  }
  dictionary[3] = c;
  w = c as string;
  result.push(c as string);
  for (;;) {
    if (data.index > length) {
      return '';
    }

    bits = 0;
    maxpower = Math.pow(2, numBits);
    power = 1;
    while (power !== maxpower) {
      resb = data.val & data.position;
      data.position >>= 1;
      if (data.position === 0) {
        data.position = resetValue;
        data.val = getNextValue(data.index++);
      }
      bits |= (resb > 0 ? 1 : 0) * power;
      power <<= 1;
    }

    let cc: number = bits;
    switch (cc) {
      case 0:
        bits = 0;
        maxpower = Math.pow(2, 8);
        power = 1;
        while (power !== maxpower) {
          resb = data.val & data.position;
          data.position >>= 1;
          if (data.position === 0) {
            data.position = resetValue;
            data.val = getNextValue(data.index++);
          }
          bits |= (resb > 0 ? 1 : 0) * power;
          power <<= 1;
        }
        dictionary[dictSize++] = String.fromCharCode(bits);
        cc = dictSize - 1;
        enlargeIn--;
        break;
      case 1:
        bits = 0;
        maxpower = Math.pow(2, 16);
        power = 1;
        while (power !== maxpower) {
          resb = data.val & data.position;
          data.position >>= 1;
          if (data.position === 0) {
            data.position = resetValue;
            data.val = getNextValue(data.index++);
          }
          bits |= (resb > 0 ? 1 : 0) * power;
          power <<= 1;
        }
        dictionary[dictSize++] = String.fromCharCode(bits);
        cc = dictSize - 1;
        enlargeIn--;
        break;
      case 2:
        return result.join('');
    }

    if (enlargeIn === 0) {
      enlargeIn = Math.pow(2, numBits);
      numBits++;
    }

    if (dictionary[cc]) {
      entry = dictionary[cc] as string;
    } else {
      if (cc === dictSize) {
        entry = w + w.charAt(0);
      } else {
        return null;
      }
    }
    result.push(entry);

    dictionary[dictSize++] = w + entry.charAt(0);
    enlargeIn--;

    w = entry;

    if (enlargeIn === 0) {
      enlargeIn = Math.pow(2, numBits);
      numBits++;
    }
  }
}
